import { Tab } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import i18next from 'i18next';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CustomDialog from '../../components/modals/CustomDialog/CustomDialog';
import { postApplication } from '../../services/application/application';
import useSnackbar from '../../utils/hooks/useSnackbar';
import Statement from '../Landing/Statement';
import Application from './Application';
import Confirm from './Confirm';
import ContactInfo from './ContactInfo';
import DocumentUpload from './DocumentUpload';
import MainInfo from './MainInfo';
import VulnerableGroup from './VulnerableGroup';
import SpecialNeeds from './SpecialNeeds';
import { getSchools } from '../../services/catalog/catalog';
import { UserContext } from '../../utils/contexts/UserContext';
// import { Button } from '../../components/forms/Buttons/Button';
import ViewForm from '../Forms/Form/View/ViewForm';
import { Button } from '../../components/forms/Buttons/Button';
import { getFormByYear } from '../../services/forms/formsService';
import SimpleLoader from '../../components/Loader/SimpleLoader';
import DraftErrorBoundary from './DraftErrorBoundary';
import {
  FILE_KEYS,
  clearDraft,
  clearStepDrafts,
  deleteDraftFiles,
  firstMissingFileStep,
  loadDraft,
  purgeDraftFiles,
  putDraftFile,
  readDraftFiles,
  repairProgress,
  saveDraft,
} from '../../utils/helpers/registrationDraft';

const TAB_STYLE =
  'w-full border-b-2 p-2 ui-selected:border-b-blue-600 ui-selected:bg-blue-50 ui-selected:text-blue-600';
const COMPLETED_TAB_STYLE =
  'w-full border-b-2 border-r border-b-green-600 bg-green-50 text-green-600 p-2';

const INITIAL_BODY = {
  first_name: '',
  middle_name: '',
  last_name: '',
  gender: null,
  avatar_photo: null,
  iin: '',
  birth_date: null,
  school: null,
  district: null,
  exam_center: null,
  address: '',
  parent_first_name: '',
  parent_middle_name: '',
  parent_last_name: '',
  parent_phone_number: '',
  parent_phone_number_2: '',
  language: null,
  current_school_name: '',
  document: null,
  social_doc_type: null,
  is_inclusive: false,
  inclusive_comments: null,
  inclusiveness: [],
  inclusive_needs: [],
  inclusiveness_document: null,
};

// Восстанавливает черновик вкладки (см. registrationDraft) до того, как
// смонтируется мастер: шаги и эффекты сохранения не должны увидеть body без
// файлов, которые ещё читаются из IndexedDB.
export default function RegistrationPage() {
  const { addSnackbar } = useSnackbar();
  const [savedDraft] = useState(() => {
    const draft = loadDraft();
    // ввод шагов без черновика — остаток прошлой регистрации
    if (!draft) clearStepDrafts();
    return draft;
  });
  // undefined — файлы черновика ещё читаются
  const [initialDraft, setInitialDraft] = useState(
    savedDraft ? undefined : null,
  );

  useEffect(() => {
    purgeDraftFiles(Object.values(savedDraft?.fileRefs ?? {}));
    if (!savedDraft) return;

    let ignore = false;
    readDraftFiles(savedDraft.fileRefs).then((files) => {
      if (ignore) return;
      const { draft, filesLost } = repairProgress({
        ...savedDraft,
        body: { ...savedDraft.body, ...files },
      });
      if (filesLost) {
        addSnackbar(i18next.t('signup.draft_files_lost'), 'error');
      }
      setInitialDraft(draft);
    });
    return () => {
      ignore = true;
    };
  }, [savedDraft, addSnackbar]);

  if (initialDraft === undefined) {
    return (
      <div className='mt-12 flex justify-center'>
        <SimpleLoader className='h-10' />
      </div>
    );
  }

  return (
    <DraftErrorBoundary>
      <Registration initialDraft={initialDraft} />
    </DraftErrorBoundary>
  );
}

function Registration({ initialDraft }) {
  const [t, i18n] = useTranslation();
  const navigate = useNavigate();

  const { user } = useContext(UserContext);
  const { addSnackbar } = useSnackbar();

  const [bilSchools, setBilSchools] = useState([]);
  const [schoolsError, setSchoolsError] = useState(null);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  // черновик появляется только после согласия — второй раз его не спрашиваем
  const [modalOpen, setModalOpen] = useState(!initialDraft);
  const [selectedBilSchool, setSelectedBilSchool] = useState(
    initialDraft?.selectedBilSchool ?? null,
  );
  const [selectedDistrict, setSelectedDistrict] = useState(
    initialDraft?.selectedDistrict ?? null,
  );
  const [selectedExamCenter, setSelectedExamCenter] = useState(
    initialDraft?.selectedExamCenter ?? null,
  );
  const [vulnerableGroup, setVulnerableGroup] = useState(
    initialDraft?.vulnerableGroup ?? null,
  );
  const [selectedIndex, setSelectedIndex] = useState(
    initialDraft?.selectedIndex ?? 0,
  );
  const completed = initialDraft?.completed ?? [];
  const [mainPartCompleted, setMainPartCompleted] = useState(!!completed[0]);
  const [contactsPartCompleted, setContactsPartCompleted] = useState(
    !!completed[1],
  );
  const [docsPartCompleted, setDocsPartCompleted] = useState(!!completed[2]);
  const [statementPartCompleted, setStatementPartCompleted] = useState(
    !!completed[3],
  );
  const [vulnerableGroupPartCompleted, setVulnerableGroupPartCompleted] =
    useState(!!completed[4]);
  const [specialNeedsPartCompleted, setSpecialNeedsPartCompleted] = useState(
    !!completed[5],
  );
  const [surveyComplete, setSurveyComplete] = useState(!!completed[6]);
  const completedSteps = useMemo(
    () => [
      mainPartCompleted,
      contactsPartCompleted,
      docsPartCompleted,
      statementPartCompleted,
      vulnerableGroupPartCompleted,
      specialNeedsPartCompleted,
      surveyComplete,
    ],
    [
      mainPartCompleted,
      contactsPartCompleted,
      docsPartCompleted,
      statementPartCompleted,
      vulnerableGroupPartCompleted,
      specialNeedsPartCompleted,
      surveyComplete,
    ],
  );
  // Опрос — своя форма на каждый год экзамена, её uuid отдаёт бэк.
  // null — в этом году опрос не проводится.
  const [surveyUuid, setSurveyUuid] = useState(null);
  const [surveyLoading, setSurveyLoading] = useState(true);
  const [surveyError, setSurveyError] = useState(null);
  // Сессия опроса — часть черновика вкладки: переживает F5 и не достаётся
  // регистрации в соседней вкладке, как было бы через общий localStorage.
  const [surveySession, setSurveySession] = useState(
    initialDraft?.surveySession ?? null,
  );
  const [body, setBody] = useState(() => ({
    ...INITIAL_BODY,
    ...initialDraft?.body,
  }));
  // id файлов body в IndexedDB: { avatar_photo: id, ... }
  const [fileRefs, setFileRefs] = useState(initialDraft?.fileRefs ?? {});
  // файлы, которые уже лежат в IndexedDB (восстановленные — тоже)
  const savedFilesRef = useRef({
    avatar_photo: body.avatar_photo,
    document: body.document,
    inclusiveness_document: body.inclusiveness_document,
  });
  // после отправки черновик удалён, эффекты не должны записать его снова
  const submittedRef = useRef(false);

  const nextPage = (step) => {
    setSelectedIndex(step);
  };

  const apply = async () => {
    // файл мог пропасть при восстановлении черновика — FormData.append упал бы
    const missingFileStep = firstMissingFileStep(
      body,
      completedSteps,
      fileRefs,
    );
    if (missingFileStep !== null) {
      addSnackbar(t('signup.draft_files_lost'), 'error');
      setSelectedIndex(missingFileStep);
      return;
    }

    setLoading(true);

    const formBody = new FormData();

    // Без сессии (опроса нет или он не открывался) раньше уходила строка "null"
    if (surveySession) {
      formBody.append('session', surveySession);
    }

    for (var key in body) {
      if (key === 'avatar_photo') {
        formBody.append('avatar_photo', body['avatar_photo'], 'avatar_photo');
      } else if (key === 'document') {
        formBody.append('document', body['document'], 'document');
      } else if (key === 'inclusiveness_document') {
        if (body['inclusiveness_document']) {
          formBody.append(
            'inclusiveness_document',
            body['inclusiveness_document'],
            'inclusiveness_document',
          );
        }
      } else if (key === 'inclusiveness' || key === 'inclusive_needs') {
        if (body[key] && body[key].length > 0) {
          formBody.append(key, JSON.stringify(body[key]));
        }
      // } else if (
      //   key === 'iin' &&
      //   ['13d17c3c-ae74-45ee-80a2-cfedc2a2c5fd'].includes(body['school'])
      // ) {
      //   formBody.append(key, `$${body[key]}`);
      } else {
        if (body[key]) {
          formBody.append(key, body[key]);
        }
      }
    }
    // console.log('formBody', body);
    await postApplication(formBody)
      .then(() => {
        setLoading(false);
        submittedRef.current = true;
        clearDraft(Object.values(fileRefs));
        addSnackbar(t('Успешно'), 'success');

        navigate('/registration/message', {
          // navigate('/qwerty/message', {
          state: {
            name: body?.first_name + ' ' + body?.last_name,
            isVulnerable: vulnerableGroup,
          },
        });
      })
      .catch((err) => {
        console.log('err', err);
        setLoading(false);
        setSelectedIndex(0);
        addSnackbar(
          err?.response?.data?.code === 1
            ? t('signup.iin_duplicate')
            : err?.response?.data?.code === 2
            ? t('welcome.registration_closed')
            : 'Ошибка',
          'error',
        );
      })
      .finally(() => {});
  };

  useEffect(() => {
    if (user?.uuid) {
      if (
        user?.roles?.some(
          (role) =>
            role.code === 'SA' ||
            role?.code === 'SSA' ||
            role?.code?.includes('RC'),
        )
      ) {
        navigate({
          pathname: `/admin/results`,
          state: { user: user },
        });
      }
      if (user?.roles?.some((role) => role.code === 'VP')) {
        navigate({
          pathname: `/results`,
          state: { user: user },
        });
      }
      if (user?.roles?.some((role) => role.code === 'STD')) {
        navigate({
          pathname: `/student/profile`,
          state: { user: user },
        });
      }
      addSnackbar('Пожалуйста, выйдите из системы', 'info');
    }

    return () => {};
  }, [user, navigate, addSnackbar]);

  useEffect(() => {
    setSchoolsLoading(true);
    getSchools({ page_size: 200 })
      .then((res) => {
        const options = res.results
          .filter(
            (sc) =>
              ![
                '043',
                '057',
                '138',
                '151',
                '115',
                '156',
                '191',
                '998', // Test school #2
                '999', // Test School
              ].includes(sc?.school_id),
          )
          .map((item) => ({
            label: item?.name,
            value: item?.uuid,
            school_id: item?.school_id,
            has_social: item?.has_social,
          }));
        setBilSchools(options);
      })
      .catch((err) => {
        console.log('err', err);
        setSchoolsError(err);
      })
      .finally(() => {
        setSchoolsLoading(false);
      });
  }, []);

  const loadSurvey = useCallback((signal) => {
    setSurveyLoading(true);
    setSurveyError(null);
    // без exam_year бэк берёт текущий год экзамена
    getFormByYear({ signal })
      .then((res) => {
        setSurveyUuid(res?.uuid ?? null);
      })
      .catch((err) => {
        if (signal?.aborted) return;
        console.log('err', err);
        setSurveyError(err);
      })
      .finally(() => {
        if (!signal?.aborted) setSurveyLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadSurvey(controller.signal);

    return () => {
      controller.abort();
    };
  }, [loadSurvey]);

  useEffect(() => {
    // до согласия сохранять нечего, после отправки черновик уже удалён
    if (modalOpen || submittedRef.current) return;
    saveDraft({
      body,
      selectedIndex,
      completed: completedSteps,
      selectedBilSchool,
      selectedDistrict,
      selectedExamCenter,
      vulnerableGroup,
      surveySession,
      fileRefs,
    });
  }, [
    modalOpen,
    body,
    selectedIndex,
    completedSteps,
    selectedBilSchool,
    selectedDistrict,
    selectedExamCenter,
    vulnerableGroup,
    surveySession,
    fileRefs,
  ]);

  // Файлы пишутся в IndexedDB, только когда меняются, а заменённые удаляются.
  useEffect(() => {
    if (submittedRef.current) return;
    FILE_KEYS.forEach((key) => {
      const file = body[key] ?? null;
      if (file === savedFilesRef.current[key]) return;
      savedFilesRef.current[key] = file;

      if (fileRefs[key]) {
        deleteDraftFiles([fileRefs[key]]);
        setFileRefs(({ [key]: replaced, ...rest }) => rest);
      }
      if (file instanceof Blob) {
        putDraftFile(file)
          .then((id) => {
            // пока писали, файл заменили или заявку уже отправили
            if (savedFilesRef.current[key] !== file || submittedRef.current) {
              deleteDraftFiles([id]);
              return;
            }
            setFileRefs((refs) => ({ ...refs, [key]: id }));
          })
          // без IndexedDB файл просто не переживёт F5
          .catch(() => {});
      }
    });
  }, [body, fileRefs]);

  return (
    <div className='mt-12 flex  justify-center bg-sheet'>
      <CustomDialog
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        closeModal={() => {
          // setModalOpen(false);
        }}
        headerText={
          <span className='font-bold'>
            {i18n?.language === 'kk'
              ? 'Үміткерді тіркеуге және қабылдау емтиханына қатыстыру жөніндегі ата-анасының немесе заңды өкілдерінің келісімі'
              : 'Согласие родителей или законных представителей на регистрацию и участие во вступительном экзамене'}
          </span>
        }
      >
        <Statement setModalOpen={setModalOpen} />
      </CustomDialog>
      <div className='mb-12 flex w-[72rem] flex-col rounded-md border'>
        {/* <div className='border-b'>
          <p className='mx-4 my-4 text-xl font-bold'>Student registration</p>
        </div> */}
        <div className='flex h-full flex-col'>
          <Tab.Group
            selectedIndex={selectedIndex}
            onChange={setSelectedIndex}
            manual
          >
            <Tab.List className='flex w-full flex-col font-semibold sm:h-20 sm:flex-row'>
              <Tab
                className={mainPartCompleted ? COMPLETED_TAB_STYLE : TAB_STYLE}
              >
                <span className='flex flex-row items-center justify-center'>
                  {t('signup.main_info')}
                  {mainPartCompleted && (
                    <CheckCircleIcon className='ml-1 h-5 w-5 min-w-fit max-w-fit' />
                  )}
                </span>
              </Tab>
              <Tab
                disabled={!mainPartCompleted}
                className={
                  contactsPartCompleted ? COMPLETED_TAB_STYLE : TAB_STYLE
                }
              >
                <span className='flex flex-row items-center justify-center'>
                  {t('signup.contact_info')}
                  {contactsPartCompleted && (
                    <CheckCircleIcon className='ml-1 h-5 w-5 min-w-fit max-w-fit' />
                  )}
                </span>
              </Tab>
              <Tab
                disabled={!contactsPartCompleted}
                className={docsPartCompleted ? COMPLETED_TAB_STYLE : TAB_STYLE}
              >
                <span className='flex flex-row items-center justify-center'>
                  {t('signup.upload_docs')}
                  {docsPartCompleted && (
                    <CheckCircleIcon className='ml-1 h-5 w-5 min-w-fit max-w-fit' />
                  )}
                </span>
              </Tab>
              <Tab
                disabled={!docsPartCompleted}
                className={
                  statementPartCompleted ? COMPLETED_TAB_STYLE : TAB_STYLE
                }
              >
                <span className='flex flex-row items-center justify-center'>
                  {t('signup.statement')}
                  {statementPartCompleted && (
                    <CheckCircleIcon className='ml-1 h-5 w-5 min-w-fit max-w-fit' />
                  )}
                </span>
              </Tab>
              <Tab
                disabled={
                  !statementPartCompleted || !selectedBilSchool?.has_social
                  // false
                }
                className={
                  vulnerableGroupPartCompleted && selectedBilSchool?.has_social
                    ? COMPLETED_TAB_STYLE
                    : TAB_STYLE
                }
              >
                <span className='flex flex-row items-center justify-center'>
                  {t('signup.asp_title')}
                  {vulnerableGroupPartCompleted && (
                    <CheckCircleIcon className='ml-1 h-5 w-5 min-w-fit max-w-fit' />
                  )}
                </span>
              </Tab>
              <Tab
                disabled={!vulnerableGroupPartCompleted}
                className={
                  specialNeedsPartCompleted ? COMPLETED_TAB_STYLE : TAB_STYLE
                }
              >
                <span className='flex flex-row items-center justify-center'>
                  {t('signup.special_needs.title')}
                  {specialNeedsPartCompleted && (
                    <CheckCircleIcon className='ml-1 h-5 w-5 min-w-fit max-w-fit' />
                  )}
                </span>
              </Tab>
              <Tab
                disabled={!specialNeedsPartCompleted}
                className={surveyComplete ? COMPLETED_TAB_STYLE : TAB_STYLE}
              >
                <span className='flex flex-row items-center justify-center'>
                  {t('signup.survey')}
                  {surveyComplete && (
                    <CheckCircleIcon className='ml-1 h-5 w-5 min-w-fit max-w-fit' />
                  )}
                </span>
              </Tab>
              <Tab
                disabled={!surveyComplete}
                className='w-full border-b-2 p-2 ui-selected:border-b-blue-600 ui-selected:bg-blue-50 ui-selected:text-blue-600'
              >
                {t('signup.signup_completion')}
              </Tab>
            </Tab.List>

            <Tab.Panels className='h-auto p-6'>
              <Tab.Panel className='h-full'>
                <MainInfo
                  applicant={body}
                  setBody={setBody}
                  nextPage={nextPage}
                  setMainPartCompleted={setMainPartCompleted}
                />
              </Tab.Panel>
              <Tab.Panel className='h-full'>
                <ContactInfo
                  applicant={body}
                  setBody={setBody}
                  nextPage={nextPage}
                  setContactsPartCompleted={setContactsPartCompleted}
                  setSelectedBilSchool={setSelectedBilSchool}
                  setSelectedDistrict={setSelectedDistrict}
                  setSelectedExamCenter={setSelectedExamCenter}
                  bilSchools={bilSchools}
                  schoolsError={schoolsError}
                  schoolsLoading={schoolsLoading}
                />
              </Tab.Panel>
              <Tab.Panel className='h-full'>
                <DocumentUpload
                  applicant={body}
                  setBody={setBody}
                  nextPage={nextPage}
                  setDocsPartCompleted={setDocsPartCompleted}
                />
              </Tab.Panel>
              <Tab.Panel className='h-full'>
                <Application
                  applicant={body}
                  nextPage={nextPage}
                  setStatementPartCompleted={setStatementPartCompleted}
                  selectedBilSchool={selectedBilSchool}
                  setVulnerableGroupPartCompleted={
                    setVulnerableGroupPartCompleted
                  }
                />
              </Tab.Panel>
              <Tab.Panel className='h-full'>
                <VulnerableGroup
                  applicant={body}
                  setBody={setBody}
                  nextPage={nextPage}
                  setVulnerableGroupPartCompleted={
                    setVulnerableGroupPartCompleted
                  }
                  setVulnerableGroup={setVulnerableGroup}
                />
              </Tab.Panel>
              <Tab.Panel className='h-full'>
                <SpecialNeeds
                  applicant={body}
                  setBody={setBody}
                  nextPage={nextPage}
                  setSpecialNeedsPartCompleted={setSpecialNeedsPartCompleted}
                />
              </Tab.Panel>
              <Tab.Panel className='h-full '>
                {surveyLoading ? (
                  <SimpleLoader className='h-10' />
                ) : surveyError ? (
                  <div className='flex flex-col items-center gap-4 py-8'>
                    <p>{t('signup.survey_load_error')}</p>
                    <Button
                      text={t('buttons.retry')}
                      onClick={() => loadSurvey()}
                    />
                  </div>
                ) : surveyUuid ? (
                  <ViewForm
                    UUID={surveyUuid}
                    session={surveySession}
                    onSessionChange={setSurveySession}
                    onSubmit={() => {
                      nextPage(7);
                      setSurveyComplete(true);
                    }}
                    SubmitMessegeComponent={() => (
                      <Button
                        text={t('buttons.next')}
                        onClick={() => {
                          nextPage(7);
                          setSurveyComplete(true);
                        }}
                      />
                    )}
                  />
                ) : (
                  <div className='flex flex-col items-center gap-4 py-8'>
                    <p>{t('signup.survey_not_held')}</p>
                    <Button
                      text={t('buttons.next')}
                      onClick={() => {
                        nextPage(7);
                        setSurveyComplete(true);
                      }}
                    />
                  </div>
                )}
                {/* <iframe
                  className='h-screen w-full'
                  title='survey'
                  src={`/forms/${surveyUuid}`}
                  frameborder='0'
                /> */}
              </Tab.Panel>
              <Tab.Panel className='h-full'>
                <Confirm
                  applicant={body}
                  selectedBilSchool={selectedBilSchool}
                  selectedDistrict={selectedDistrict}
                  selectedExamCenter={selectedExamCenter}
                  vulnerableGroup={vulnerableGroup}
                  apply={apply}
                  loading={loading}
                />
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
    </div>
  );
}
